// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || "/portal";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate(redirectTo, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/portal";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex bg-[#FAFAF8]">
      <div className="hidden lg:flex w-1/2 relative overflow-hidden">
        <img src="https://customer-assets.emergentagent.com/job_dftb26-virtual/artifacts/egl6ysm6_DFTB3.png"
             alt="DFTB conference" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0F172A]/80 via-[#0F172A]/30 to-transparent" />
        <div className="relative p-12 flex flex-col justify-between text-white w-full">
          <Link to="/" className="flex items-center gap-3" data-testid="login-logo">
            <div className="leading-none">
              <div className="flex items-baseline gap-0.5">
                <span className="font-heading text-2xl font-black text-white tracking-tight">DFTB</span>
                <span className="font-heading text-2xl font-black text-[#28BBE3] tracking-tight">26</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-white/70 font-bold mt-1">Virtual</div>
            </div>
          </Link>
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-white/70 font-semibold">22-24 June 2026 · SEC Glasgow</div>
            <h2 className="mt-3 font-heading text-4xl tracking-tight font-black leading-tight max-w-md">
              Back To Basics. The acute paediatric conference, streamed to your screen.
            </h2>
            <p className="mt-3 text-white/70 max-w-md text-sm">
              Sign in to access the live stream and your DFTB26 portal replays.
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 sm:p-12">
        <div className="w-full max-w-sm">
          <Link to="/" className="lg:hidden flex items-center gap-3 mb-10">
            <div className="leading-none">
              <div className="flex items-baseline gap-0.5">
                <span className="font-heading text-2xl font-black text-[#212121] tracking-tight">DFTB</span>
                <span className="font-heading text-2xl font-black text-[#28BBE3] tracking-tight">26</span>
              </div>
            </div>
          </Link>

          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Sign in</div>
          <h1 className="mt-2 font-heading text-3xl sm:text-4xl tracking-tighter font-medium text-slate-900">
            Welcome back.
          </h1>

          <Button onClick={googleLogin} variant="outline"
            className="mt-8 w-full rounded-full border-slate-300 text-slate-800 py-6 hover:bg-slate-50"
            data-testid="login-google-btn">
            <svg className="mr-2" width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34.6 6 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 19 13 24 13c3 0 5.8 1.1 7.9 3l5.7-5.7C34.6 7 29.6 5 24 5 16.3 5 9.6 9.1 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.5 0 10.5-2.1 14.2-5.5l-6.6-5.4c-2.1 1.5-4.8 2.4-7.6 2.4-5.3 0-9.7-3.3-11.3-8L6.1 32C9.3 38.4 16.1 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.4-2.4 4.5-4.5 5.9l6.6 5.4C40 36 44 30.5 44 24c0-1.2-.1-2.3-.4-3.5z"/></svg>
            Continue with Google
          </Button>

          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold">or</span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-2 rounded-2xl border-slate-200 bg-white py-6" placeholder="you@hospital.org"
                data-testid="login-email-input" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Password</Label>
                <Link to="/forgot-password" className="text-xs text-slate-500 hover:text-[#28BBE3] hover:underline" data-testid="login-forgot-link">
                  Forgot password?
                </Link>
              </div>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-2 rounded-2xl border-slate-200 bg-white py-6" placeholder="••••••••"
                data-testid="login-password-input" />
            </div>
            <Button type="submit" disabled={loading}
              className="w-full rounded-full bg-[#28BBE3] hover:bg-[#1FA8CC] text-white py-6 font-semibold"
              data-testid="login-submit-btn">
              {loading ? "Signing in…" : "Sign in"}
              <ArrowRight className="ml-2" size={16} />
            </Button>
          </form>

          <p className="mt-8 text-sm text-slate-600 text-center">
            Just bought a ticket? Your login was emailed to you.
          </p>
          <p className="mt-3 text-sm text-slate-600 text-center">
            Don't have a ticket yet?{" "}
            <Link to="/#tickets" className="text-[#28BBE3] hover:underline font-semibold" data-testid="login-buy-ticket-link">
              Buy one here
            </Link>
          </p>
          <p className="mt-3 text-xs text-slate-400 text-center">
            Need to create an account without a ticket?{" "}
            <Link to="/register" className="underline" data-testid="login-register-link">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
